import { useCallback } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	type GetAllPromptsResponse,
	type GetPromptResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

type UsePromptsControllerProps = {
	enabled?: boolean;
} & AssistanceServiceInjectedParams;

export type UsePromptsControllerResult = {
	data: GetAllPromptsResponse | null;
	isFetching: boolean;
	error: ReturnType<typeof useQuery>['error'];
	refetch: () => Promise<GetAllPromptsResponse | null>;
	/** Optimistically update a prompt's isFavorited status in the cache without refetching */
	updatePromptFavoriteInCache: (promptId: string, isFavorited: boolean) => void;
	/** Optimistically update multiple prompts' isFavorited status in the cache without refetching */
	bulkUpdatePromptFavoritesInCache: (
		updates: Array<{ promptId: string; isFavorited: boolean }>,
	) => void;
};

/**
 * Fetches list of prompts
 */
export const usePromptsController: (
	props: Omit<UsePromptsControllerProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UsePromptsControllerResult = createHookWithAssistanceService(
	({
		enabled = true,
		assistanceServiceInjectedParams: { assistanceService, touchpointSource },
	}: UsePromptsControllerProps): UsePromptsControllerResult => {
		const experienceTracker = useAIMateExperienceTracker({
			touchpointSource: touchpointSource ?? 'promptLibrary',
		});

		const queryResult = useQuery({
			queryKey: ['fetchPrompts'],
			queryFn: async () => {
				experienceTracker.start({
					name: ExperienceName.FETCH_PROMPTS,
					id: ExperienceName.FETCH_PROMPTS,
				});
				try {
					const result = await assistanceService.getPrompts();
					experienceTracker.succeed({
						name: ExperienceName.FETCH_PROMPTS,
					});
					return result;
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_PROMPTS,
						error: error instanceof Error ? error : new Error(String(error)),
					});
					throw error;
				}
			},
			enabled,
		});

		const updatePromptFavoriteInCache = useCallback(
			(promptId: string, isFavorited: boolean) => {
				const currentData = queryResult.data;
				if (!currentData) {
					return;
				}

				// Handle both response formats: { prompts: [...] } and { items: [...] }.
				// The `{ items: [...] }` variant is a legacy response shape not present on
				// `GetAllPromptsResponse`, so the element type is asserted after the `in` guard.
				const prompts =
					currentData.prompts ??
					('items' in currentData ? (currentData as { items: GetPromptResponse[] }).items : []);

				const updatedPrompts = prompts.map((prompt: GetPromptResponse) =>
					prompt.id === promptId ? { ...prompt, isFavorited } : prompt,
				);

				// Update the cache with the modified data
				queryResult.updateCache({
					...currentData,
					prompts: currentData.prompts ? updatedPrompts : undefined,
					...('items' in currentData ? { items: updatedPrompts } : {}),
				} as GetAllPromptsResponse);
			},
			[queryResult],
		);

		const bulkUpdatePromptFavoritesInCache = useCallback(
			(updates: Array<{ promptId: string; isFavorited: boolean }>) => {
				const currentData = queryResult.data;
				if (!currentData) {
					return;
				}

				// Create a map for quick lookup of updates
				const updatesMap = new Map(updates.map((u) => [u.promptId, u.isFavorited]));

				// Handle both response formats: { prompts: [...] } and { items: [...] }.
				// The `{ items: [...] }` variant is a legacy response shape not present on
				// `GetAllPromptsResponse`, so the element type is asserted after the `in` guard.
				const prompts =
					currentData.prompts ??
					('items' in currentData ? (currentData as { items: GetPromptResponse[] }).items : []);

				const updatedPrompts = prompts.map((prompt: GetPromptResponse) =>
					updatesMap.has(prompt.id)
						? { ...prompt, isFavorited: updatesMap.get(prompt.id) }
						: prompt,
				);

				// Update the cache with the modified data
				queryResult.updateCache({
					...currentData,
					prompts: currentData.prompts ? updatedPrompts : undefined,
					...('items' in currentData ? { items: updatedPrompts } : {}),
				} as GetAllPromptsResponse);
			},
			[queryResult],
		);

		return { ...queryResult, updatePromptFavoriteInCache, bulkUpdatePromptFavoritesInCache };
	},
);
